import "@rtsp-player/player";

declare global {
  interface Window {
    RTSP_PLAYER_CONFIG?: {
      bridgeUrl?: string;
      defaultTransport?: "tcp" | "udp";
      /** Shared auth token for the bridge, if it has AUTH_TOKENS configured. */
      token?: string;
    };
  }
}

const config = window.RTSP_PLAYER_CONFIG ?? {};
const app = document.getElementById("app")!;

interface Target {
  src: string;
  transport: "tcp" | "udp";
  bridgeUrl: string;
  token?: string;
}

/**
 * The RTSP url is the page address itself: https://host/<rtsp-url-without-scheme>[?query]
 * e.g. https://host/admin:pass@192.168.1.20:554/cam/realmonitor?channel=1&subtype=0
 * plays rtsp://admin:pass@192.168.1.20:554/cam/realmonitor?channel=1&subtype=0
 *
 * A "?src=" query param is also accepted (useful on hosts without a
 * catch-all URL-rewrite rule): https://host/?src=<rtsp-url>&transport=udp
 *
 * In path mode the entire query string belongs to the RTSP url's own query
 * (e.g. "?channel=1&subtype=0"), so transport/bridge/token can only come
 * from "?src=" mode or config.js there — never read out of that query.
 */
function resolveTarget(): Target | null {
  const query = new URLSearchParams(location.search);
  const explicitSrc = query.get("src");

  let rawTarget: string;
  let transport: "tcp" | "udp";
  let bridgeUrl: string | undefined;
  let token: string | undefined;

  if (explicitSrc) {
    rawTarget = explicitSrc;
    transport = (query.get("transport") as "tcp" | "udp" | null) ?? config.defaultTransport ?? "tcp";
    bridgeUrl = query.get("bridge") ?? config.bridgeUrl;
    token = query.get("token") ?? config.token;
  } else {
    const rawPath = decodeURIComponentSafe(location.pathname.slice(1));
    if (!rawPath) return null;
    rawTarget = rawPath + location.search;
    transport = config.defaultTransport ?? "tcp";
    bridgeUrl = config.bridgeUrl;
    token = config.token;
  }

  if (!bridgeUrl) return null;

  const src = rawTarget.startsWith("rtsp://") ? rawTarget : `rtsp://${rawTarget}`;
  return { src, transport, bridgeUrl, token };
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function renderFatal(text: string): void {
  const el = document.createElement("div");
  el.id = "message";
  el.textContent = text;
  app.appendChild(el);
}

function renderPlayer(target: Target): void {
  const player = document.createElement("rtsp-player");
  player.setAttribute("bridge-url", target.bridgeUrl);
  player.setAttribute("transport", target.transport);
  player.setAttribute("src", target.src);
  if (target.token) player.setAttribute("token", target.token);

  const overlay = document.createElement("div");
  overlay.id = "message";
  overlay.textContent = "Connecting…";

  player.addEventListener("status", (event) => {
    const { status, message } = (event as CustomEvent).detail;
    if (status === "playing") {
      overlay.remove();
    } else if (status === "error") {
      overlay.textContent = `Playback error${message ? `:\n${message}` : ""}`;
      app.appendChild(overlay);
    } else if (status === "connecting") {
      overlay.textContent = "Connecting…";
      app.appendChild(overlay);
    }
  });

  app.append(player, overlay);
}

const target = resolveTarget();

if (!target) {
  renderFatal(
    "No RTSP stream specified.\n\n" +
      "Use https://<host>/<rtsp-url-without-scheme> (e.g. /192.168.1.20:554/cam/1)\n" +
      "or https://<host>/?src=rtsp://192.168.1.20:554/cam/1\n\n" +
      (config.bridgeUrl ? "" : "config.js is missing bridgeUrl."),
  );
} else {
  renderPlayer(target);
}
